import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import {
  ChatbotReferences,
  DeleteChatbotReference,
  UpdateChatbotReference,
  UploadChatbotReference,
} from "../../../services/restaurant.services";
import {
  CHATBOT_REFERENCE_IMAGE_LIMIT,
  CHATBOT_REFERENCE_IMAGE_MAX_BYTES,
} from "../../../../restaurant/pages/chatbot/constants";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import "../chatbot-management.css";
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { ReactSortable } = require("react-sortablejs");

interface ChatbotReferenceItem {
  id: string;
  _id: string;
  image_url: string;
  display_order?: number;
}

interface ChatbotReferencesResponse {
  data?: ChatbotReferenceItem[];
}

interface ReferenceGalleryProps {
  restaurantId?: string | null;
  inline?: boolean;
}

const ReferenceGallery = ({
  restaurantId = null,
  inline = false,
}: ReferenceGalleryProps): React.ReactElement => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ChatbotReferenceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const orderRef = useRef<ChatbotReferenceItem[]>([]);
  const isAtReferenceLimit = items.length >= CHATBOT_REFERENCE_IMAGE_LIMIT;

  const load = async (): Promise<void> => {
    const response = await ChatbotReferences(restaurantId) as ChatbotReferencesResponse;
    const nextItems = (response?.data || []).map((item) => ({ ...item, id: item._id }));
    setItems(nextItems);
    orderRef.current = nextItems;
  };

  useEffect(() => {
    void load();
  }, [restaurantId]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    const remainingSlots = Math.max(CHATBOT_REFERENCE_IMAGE_LIMIT - items.length, 0);
    if (!files.length) return;
    if (remainingSlots <= 0) {
      void Swal.fire({
        icon: "error",
        text: t("chatbot_reference_upload_limit_full", { count: CHATBOT_REFERENCE_IMAGE_LIMIT }),
        ...SWAL_SETTINGS,
      } as any);
      return;
    }
    if (files.length > remainingSlots) {
      void Swal.fire({
        icon: "error",
        text: t("chatbot_reference_upload_limit_selection", {
          count: CHATBOT_REFERENCE_IMAGE_LIMIT,
          remaining: remainingSlots,
          selected: files.length,
        }),
        ...SWAL_SETTINGS,
      } as any);
      return;
    }

    const validFiles = files.filter((file) => file.size <= CHATBOT_REFERENCE_IMAGE_MAX_BYTES);
    if (!validFiles.length) return;
    setLoading(true);
    try {
      await Promise.all(validFiles.map((file) => UploadChatbotReference(restaurantId, file)));
      await load();
    } catch {
      void Swal.fire({
        icon: "error",
        text: t("common_something_went_wrong", { defaultValue: "Something went wrong." }),
        ...SWAL_SETTINGS,
      } as any);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await DeleteChatbotReference(restaurantId, id);
      await load();
    } catch {
      void Swal.fire({
        icon: "error",
        text: t("common_something_went_wrong", { defaultValue: "Something went wrong." }),
        ...SWAL_SETTINGS,
      } as any);
    }
  };

  const persistOrder = async (nextItems: ChatbotReferenceItem[]): Promise<void> => {
    setSavingOrder(true);
    try {
      await Promise.all(
        nextItems.map((item, index) => UpdateChatbotReference(restaurantId, item._id, { display_order: index }))
      );
      await load();
    } finally {
      setSavingOrder(false);
    }
  };

  const title = restaurantId
    ? t("admin_chatbot_restaurant_references", { defaultValue: "Restaurant References" })
    : t("admin_chatbot_global_references", { defaultValue: "Global references" });

  const inner = (
    <>
      <div className="cbm-panel-title">{title}</div>
      <div className="cbm-panel-sub">
        <ul className="cbm-ref-sub-list">
          <li>{t("chatbot_reference_order_hint_priority")}</li>
          <li>{t("chatbot_reference_order_hint_precedence")}</li>
        </ul>
      </div>

      <div className="cbm-ref-grid">
        <ReactSortable
          list={items}
          setList={(nextItems: ChatbotReferenceItem[]) => {
            setItems(nextItems);
            orderRef.current = nextItems;
          }}
          className="cbm-ref-sortable-list"
          handle=".cbm-ref-drag-handle"
          animation={180}
          disabled={loading || savingOrder}
          onEnd={() => {
            void persistOrder(orderRef.current);
          }}
        >
          {items.map((item, index) => (
            <div className="cbm-ref-card" key={item._id}>
              <div className={`cbm-ref-rank${index === 0 ? " primary" : ""}`}>{index + 1}</div>
              <img src={item.image_url} alt={t("chatbot_references")} />
              <button
                className="cbm-ref-delete"
                type="button"
                onClick={() => {
                  void handleDelete(item._id);
                }}
                aria-label={t("chatbot_delete_reference", { defaultValue: "Delete reference" })}
              >
                <i className="ti ti-trash" />
              </button>
              <div className="cbm-ref-drag-handle" aria-hidden="true">
                <i className="ti ti-grip-vertical" />
              </div>
            </div>
          ))}
        </ReactSortable>

        {!isAtReferenceLimit ? (
          <button
            className="cbm-ref-upload"
            type="button"
            disabled={loading || savingOrder}
            onClick={() => input.current?.click()}
          >
            {loading ? (
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            ) : (
              <>
                <i className="ti ti-upload" />
                <span>{t("chatbot_upload")}</span>
              </>
            )}
          </button>
        ) : null}
      </div>

      {!items.length && !loading && (
        <div style={{ fontSize: 13, color: "#aaa", marginTop: 12 }}>
          {t("chatbot_upload_empty_state_text")}
        </div>
      )}

      <input
        ref={input}
        hidden
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={(event) => {
          void handleUpload(event);
        }}
      />
    </>
  );

  if (inline) return inner;

  return (
    <div className="cbm-panel" style={{ marginBottom: 16 }}>
      {inner}
    </div>
  );
};

export default ReferenceGallery;
